/* eslint-disable react/jsx-props-no-spreading */
import React, { ImgHTMLAttributes, useState } from 'react';
import { BlurhashCanvas } from 'react-blurhash';
import LazyLoad from 'react-lazyload';
import { getCurrentHub } from '@sentry/nextjs';
import ContentComposer, { ComponentProps } from '../../Internals/ContentComposer';
import * as S from './styles';

export interface CustomImageComponent extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  blurhash?: string;
  width?: number;
  height?: number;
  alt?: string;
  figcaption?: string | string[] | ComponentProps[];
  /**
   * Srcset legado em formato objeto (chave→URL) usado pela capa via HeaderImage.
   * O dicionário de breakpoints fica em `HeaderImage/index.tsx:srcSetDictionary`.
   */
  srcset?: { [key: number]: string };
  /**
   * Srcset string pronta ("url 320w, url 640w, ..."). Usado pelas imagens inline
   * do corpo da matéria, onde `directus-base:process-materia-content` já assina
   * as 5 derivadas e monta a string com os breakpoints canônicos.
   */
  srcsetString?: string;
  sizes?: string;
  editorialCategory?: 'ctup' | 'cteletro' | 'ctauto';
  isHeader?: 'internal' | 'full';
  objectFit?: 'object-cover' | 'object-contain' | 'object-fill' | 'object-none';
  rounded?: boolean;
}

export default function ImageComponent({
  src,
  blurhash = undefined,
  alt,
  title = undefined,
  figcaption = undefined,
  srcset = undefined,
  srcsetString = undefined,
  sizes = undefined,
  width = undefined,
  height = undefined,
  objectFit = 'object-cover',
  rounded = true,
}: CustomImageComponent) {
  const currentSpan = getCurrentHub().getScope()?.getSpan();
  const imageComponentSpan = currentSpan && currentSpan.startChild({ op: 'imageComponent', description: src });

  const [hasLoaded, setHasLoaded] = useState(false);

  // F2.5: prioriza `srcsetString` quando presente (inline corpo).
  const finalSrcset = srcsetString
    ?? (srcset ? Object.keys(srcset).map(e => `${srcset[e]} ${e}w`).join(', ') : undefined);
  const finalSizes = sizes
    ?? (srcset ? Object.keys(srcset).map(e => `(max-width: ${e}px) ${e}px`).join(', ') : undefined);

  // F2.5: quando `srcsetString` está setado (caminho inline), as dimensões
  // intrínsecas da imagem podem ser enormes (ex.: 6000×4000). Não propagar para
  // o `<figure>` — Tailwind `w-full` controla o container. Mantemos o
  // comportamento legado (width/height vão pro container) só para o caminho
  // capa/card, que passa dimensões pequenas e historicamente pinta a área.
  const inlineMode = srcsetString !== undefined;
  const containerStyle: React.CSSProperties | undefined = inlineMode
    ? undefined
    : { width: width || '100%', height: height || '100%' };

  // Inline: placeholder reserva a área pela proporção, não pela altura intrínseca (2592px abriria buraco no mobile).
  const intrinsicRatio = width && height ? `${width} / ${height}` : undefined;

  const inlinePlaceholder =
    inlineMode && intrinsicRatio ? <div aria-hidden className="w-full" style={{ aspectRatio: intrinsicRatio }} /> : undefined;

  // O CSS estica o canvas do blurhash pro tamanho visual; cap de resolução evita decodar na dimensão intrínseca.
  const blurhashWidth = width ? Math.min(width, 64) : 64;
  const blurhashHeight = width && height ? Math.max(1, Math.round(blurhashWidth * (height / width))) : 36;

  return (
    <S.ContainerImage style={containerStyle} rounded={rounded}>
      <LazyLoad className="h-full rounded" height={inlinePlaceholder ? undefined : height || 1} placeholder={inlinePlaceholder}>
        <img
          src={src}
          {...(finalSrcset ? { srcSet: finalSrcset } : {})}
          {...(finalSizes ? { sizes: finalSizes } : {})}
          alt={alt}
          title={title || alt}
          onLoad={() => setHasLoaded(true)}
          width={width && width}
          height={height && height}
          style={!hasLoaded ? { display: 'none' } : null}
          // `max-w-full` é defensivo: quando o caller (ex.: imagem inline do conteúdo
          // editorial) não passa width/height nem srcset, o browser renderia no naturalWidth
          // (4000px+ no Thumbor com `width=0`) e estouraria o container do artigo (~700px).
          // Não usar `h-auto` pq sobrescreveria o atributo HTML `height` em cards 1:1 forçados
          // (Product/Header, FeaturedDeals) — sem altura explícita, browser preserva aspect natural.
          className={`max-w-full ${objectFit}`}
        />
        {!hasLoaded && blurhash && <BlurhashCanvas hash={blurhash} width={blurhashWidth} height={blurhashHeight} punch={1} />}

        {figcaption && (
          <S.CaptionImage>
            {figcaption && figcaption !== undefined && figcaption.length > 0 && typeof figcaption === 'string'
              ? figcaption
              : Array.isArray(figcaption) &&
                figcaption?.map((figcapitem, id) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <React.Fragment key={`${typeof figcapitem === 'string' ? figcapitem : figcapitem.valor}-${id}`}>
                    {figcapitem && typeof figcapitem === 'string' && figcapitem?.length > 0
                      ? figcapitem.charAt(0).toUpperCase() + figcapitem.slice(1)
                      : typeof figcapitem === 'object' && (
                          <ContentComposer
                            tipo={figcapitem?.tipo}
                            url={figcapitem.url}
                            as={figcapitem?.tag}
                            valor={figcapitem?.valor}
                            target={figcapitem.target}
                          />
                        )}
                  </React.Fragment>
                ))}
          </S.CaptionImage>
        )}
        {imageComponentSpan && imageComponentSpan.finish()}
      </LazyLoad>
    </S.ContainerImage>
  );
}
